import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import SongCategorySettingsPicker from "../Pickers/SongCategorySettingsPicker";
import { useState } from "react";
import { CardChecklist, CheckLg, ExclamationTriangleFill, PencilFill, TrashFill, XLg } from "react-bootstrap-icons";
import ConfirmPrompt from "../ConfirmPrompt";
import { PLAP_CATEGORY_SIZES } from "../constants";
import RATooltip from "../RATooltip";

export default function PlaylistAutopilotSettingsCategoryRow({ virtualizer, item, categories, setupIssues, dataHandlers, onRulesClick, ...props }) {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const editing = item.status === "EDIT" || item.status === "ADD";

    return (
        <Row className="py-1 border-bottom position-absolute w-100 hoverable-no-pointer" ref={virtualizer.measureElement} {...props}>
            <ConfirmPrompt
                bodyText={
                    <span>
                        Are you sure you want to delete{" "}
                        <span className=" fw-bold">
                            [{item.code}] {item.name}
                        </span>
                        ?
                    </span>
                }
                cancelText="No, go back"
                handleConfirm={async () => await dataHandlers.remove(item)}
                handleClose={() => setShowDeleteConfirm(false)}
                show={showDeleteConfirm}
            />
            <Col xs="3" className=" align-content-center">
                <Row>
                    <Col xs="3">
                        <Form.Control
                            value={item.code}
                            plaintext={!editing}
                            readOnly={!editing}
                            size="sm"
                            className="text-center fw-bold"
                            onChange={e => dataHandlers.change(item, "code", e.target.value)}
                        />
                    </Col>
                    <Col className="d-flex column-gap-2 align-items-center">
                        {setupIssues?.length > 0 && (
                            <RATooltip
                                size={28}
                                tooltipElement={<ExclamationTriangleFill />}
                                colorClass={setupIssues.find(x => x.type === "error") ? "danger" : "warning"}>
                                {setupIssues.map((x, i) => (
                                    <div key={i}>{x.error_message}</div>
                                ))}
                            </RATooltip>
                        )}
                        <Form.Control
                            value={item.name}
                            readOnly={!editing}
                            plaintext={!editing}
                            size="sm"
                            onChange={e => dataHandlers.change(item, "name", e.target.value)}
                        />
                    </Col>
                </Row>
            </Col>
            <Col xs="4" className=" align-content-center">
                <Row>
                    <Col xs="4" className=" align-self-center text-center">
                        <Form.Select
                            value={item.size_type}
                            size="sm"
                            readOnly={!editing}
                            onChange={e => dataHandlers.change(item, "size_type", e.target.value)}>
                            {Object.entries(PLAP_CATEGORY_SIZES).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs="3" className=" align-self-center text-center">
                        <Form.Control
                            type="number"
                            min={0}
                            value={item.size_type === "target" ? item.target_size ?? "" : ""}
                            plaintext={!editing}
                            readOnly={!editing}
                            disabled={item.size_type !== "target"}
                            size="sm"
                            className="text-center"
                            onChange={e => dataHandlers.change(item, "target_size", e.target.value || null)}
                        />
                    </Col>
                    <Col xs="5">
                        <Row>
                            <Col xs="6" className=" align-content-center">
                                <Form.Control
                                    type="number"
                                    min={0}
                                    value={item.flexibility_size_absolute ?? ""}
                                    plaintext={!editing}
                                    readOnly={!editing}
                                    size="sm"
                                    className="text-center"
                                    onChange={e => dataHandlers.change(item, "flexibility_size_absolute", e.target.value || null)}
                                />
                            </Col>
                            <Col xs="6" className=" align-content-center">
                                <Form.Control
                                    type="number"
                                    min={0}
                                    value={item.flexibility_size_percentage ?? ""}
                                    plaintext={!editing}
                                    readOnly={!editing}
                                    size="sm"
                                    className="text-center"
                                    onChange={e => dataHandlers.change(item, "flexibility_size_percentage", e.target.value || null)}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>
            <Col xs="4" className=" align-content-center text-center">
                <Row>
                    <Col xs="4" className=" align-self-center">
                        <SongCategorySettingsPicker
                            categories={categories}
                            value={item.move_up_code ?? ""}
                            onChange={e => dataHandlers.change(item, "move_up_code", e.target.value || null)}
                            readOnly={!editing}
                        />
                    </Col>
                    <Col xs="4" className=" align-self-center">
                        <SongCategorySettingsPicker
                            categories={categories}
                            value={item.move_down_code ?? ""}
                            onChange={e => dataHandlers.change(item, "move_down_code", e.target.value || null)}
                            readOnly={!editing}
                        />
                    </Col>
                    <Col xs="4" className=" align-self-center">
                        <SongCategorySettingsPicker
                            categories={categories}
                            value={item.move_out_code ?? ""}
                            onChange={e => dataHandlers.change(item, "move_out_code", e.target.value || null)}
                            readOnly={!editing}
                        />
                    </Col>
                </Row>
            </Col>
            <Col xs="1" className="d-flex justify-content-center">
                <ButtonGroup size="sm">
                    {!editing ? (
                        <>
                            <Button
                                variant="ra-yellow"
                                onClick={onRulesClick}
                                title="Edit Rules"
                                disabled={!(item.move_down_code || item.move_up_code || item.move_out_code)}>
                                <CardChecklist size={20} />
                            </Button>
                            <Button variant="ra-yellow" title="Edit" onClick={() => dataHandlers.toggleEdit(item)}>
                                <PencilFill size={20} />
                            </Button>
                            <Button variant="danger" title="Delete" onClick={() => setShowDeleteConfirm(true)}>
                                <TrashFill size={20} />
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="success" title="Save" onClick={async () => await dataHandlers.save(item)}>
                                <CheckLg size={20} />
                            </Button>
                            <Button variant="danger" title="Discard" onClick={() => dataHandlers.discard(item)}>
                                <XLg size={20} />
                            </Button>
                        </>
                    )}
                </ButtonGroup>
            </Col>
        </Row>
    );
}
